import { ChangeEvent, useState } from 'react';
import { ProcessedCallHistory } from '../../ContactManager.utils';
import { LoaderCircle, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { Button, Chip, Select } from 'common/components';
import { FileText, SquarePlay, Phone } from 'lucide-react';
import { formatQualifications } from 'utils/livekitContext.utils';
import { CallInfo } from 'types/agentCommandCenter.type';
import { Accordion } from 'components/Accordion/Accordion';
import { AggregatedQualificationsAccordion } from 'components/AggregatedQualificationsAccordion';
import { AgregatedQualificationsResponse, QualificationsResponse } from 'services/api/AgentCenterAPI';
import { DispositionType } from 'types/call.type';
import { useUpdateCallDisposition } from 'services/hooks/mutations';
import { toast } from 'react-toastify';
import { formatDisposition } from 'pages/AgenticWorkflowDetail/utils';

import styles from '../../ContactManager.module.scss';

type CallItemProps = {
    onViewTranscript: (id: string, callInfo: CallInfo) => void;
    onListenRecording: (id: string, callInfo: CallInfo) => void;
    contactName: string;
    callDispositions: DispositionType[];
    hasManagerRole: boolean;
    batchQualificationsData?: {
        qualifications: Record<string, QualificationsResponse[]>;
    };
    isBatchQualificationsLoading: boolean;
    batchAggregatedQualificationsData?: {
        qualifications: Record<string, AgregatedQualificationsResponse>;
    };
    isBatchAggregatedQualificationsLoading: boolean;
} & ProcessedCallHistory;

export const CallItem = ({
    id,
    date,
    callDirection,
    status,
    duration,
    disposition,
    aggregatedTagsUsed = false,
    onViewTranscript,
    onListenRecording,
    contactName,
    callDispositions,
    hasManagerRole,
    batchQualificationsData,
    isBatchQualificationsLoading,
    batchAggregatedQualificationsData,
    isBatchAggregatedQualificationsLoading,
}: CallItemProps) => {
    const [localDisposition, setLocalDisposition] = useState<DispositionType | undefined>(disposition);
    const { updateCallDisposition, isPending: isUpdateCallDispositionPending } = useUpdateCallDisposition();
    const formattedQualifications = formatQualifications(batchQualificationsData?.qualifications?.[id] || []);

    const handleDispositionChange = (event: ChangeEvent<HTMLSelectElement>) => {
        updateCallDisposition(
            { callId: id, disposition: event.target.value },
            {
                onSuccess: () => {
                    toast.success('Call disposition updated successfully');
                    setLocalDisposition(event.target.value as DispositionType);
                },
                onError: () => {
                    toast.error('Failed to update call disposition');
                },
            }
        );
    };

    return (
        <div className={styles.timelineItem} key={id}>
            <div className={styles.timelineDate}>
                <div className='flex items-center justify-between gap-sm'>
                    <div className='font-semibold flex items-center justify-between gap-sm'>
                        <Phone size={16} className='text-primary' />
                        <span className='text-primary font-semibold'>Call </span>
                        <span className='text-gray-500'>|</span>
                        <span>
                            {date.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            })}
                        </span>
                    </div>
                    <div className='flex items-center gap-sm'>
                        <Button
                            variant='ghost'
                            size='sm'
                            iconOnly
                            isRounded
                            icon={FileText}
                            onClick={() =>
                                onViewTranscript(id, {
                                    name: contactName,
                                    callDate: date.toLocaleDateString(),
                                    callTime: date.toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    }),
                                })
                            }
                            title='View transcript'
                        />
                        <Button
                            variant='ghost'
                            size='sm'
                            iconOnly
                            isRounded
                            icon={SquarePlay}
                            onClick={() =>
                                onListenRecording(id, {
                                    name: contactName,
                                    callDate: date.toLocaleDateString(),
                                    callTime: date.toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    }),
                                })
                            }
                            title='Listen to recording'
                        />
                    </div>
                </div>
                <div className='flex items-center justify-between gap-sm mt-sm'>
                    <div>
                        {callDirection === 'INBOUND' && (
                            <PhoneIncoming
                                size={14}
                                className={`${styles.callDirectionIcon} ${styles.inbound}`}
                                fill='currentColor'
                            />
                        )}
                        {callDirection === 'OUTBOUND' && (
                            <PhoneOutgoing
                                size={14}
                                className={`${styles.callDirectionIcon} ${styles.outbound}`}
                                fill='currentColor'
                            />
                        )}
                        {date.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </div>
                    <div>
                        <span className={`${styles.callStatusBadge} ${styles[status.toLowerCase().replace(/_/g, '')]}`}>
                            {status.replace(/_/g, ' ')}
                        </span>
                    </div>
                </div>
            </div>
            <div className={styles.timelineContent}>
                <div className={styles.callDetails}>
                    <div className={styles.callDetailItem}>
                        <span className={styles.callDetailLabel}>Direction:</span> {callDirection}
                    </div>
                    <div className={styles.callDetailItem}>
                        <span className={styles.callDetailLabel}>Duration:</span> {duration}
                    </div>

                    <div className={styles.callDetailItem}>
                        <span className={styles.callDetailLabel}>Disposition:</span>
                        {hasManagerRole ? (
                            <Select
                                size='sm'
                                options={callDispositions?.map((disposition) => ({
                                    label: formatDisposition(disposition),
                                    value: disposition,
                                }))}
                                value={localDisposition}
                                onChange={handleDispositionChange}
                                isLoading={isUpdateCallDispositionPending}
                            />
                        ) : (
                            <span>{formatDisposition(disposition)}</span>
                        )}
                    </div>
                </div>
            </div>

            <div className='flex items-center gap-md mt-md flex-wrap'>
                {aggregatedTagsUsed ? (
                    <AggregatedQualificationsAccordion
                        qualifications={batchAggregatedQualificationsData?.qualifications?.[id]}
                        isLoading={isBatchAggregatedQualificationsLoading}
                    />
                ) : (
                    <Accordion header='Qualifications' className={styles.accordion}>
                        {isBatchQualificationsLoading && (
                            <div className='flex items-center gap-md animate-pulse'>
                                <LoaderCircle size={16} className={styles.spinner} color='var(--primary-purple)' />
                                <span className='text-sm text-gray-500'>Fetching qualifications...</span>
                            </div>
                        )}
                        {!formattedQualifications.length && !isBatchQualificationsLoading && (
                            <span className='text-sm text-gray-500'>No qualifications found</span>
                        )}
                        <div className='flex flex-col items-center gap-md w-full'>
                            {formattedQualifications.map(({ key, label, value, isTrue, isFalse }) => (
                                <Chip
                                    variant='outlined'
                                    color={isTrue ? 'success' : isFalse ? 'error' : 'warning'}
                                    size='sm'
                                    corner='rounded'
                                    weight='normal'
                                    key={key}
                                    className='w-full block'
                                >
                                    <div className='flex items-center justify-between gap-sm w-full text-gray-900'>
                                        <span>{label}</span> <span>{value}</span>
                                    </div>
                                </Chip>
                            ))}
                        </div>
                    </Accordion>
                )}
            </div>
        </div>
    );
};
